// sw-register.ts — registers the hand-written /sw.js (see public/sw.js). Only in
// production builds over a secure context (D-008: Caddy internal CA on the LAN); plain
// `vite dev` over http://localhost also qualifies as a secure context for testing.
//
// An iOS home-screen install never gets the "close every tab to update" nudge a normal
// browser tab gets — it's just an app icon that silently keeps running whatever was
// cached whenever it was last opened. So beyond the one-time register(), this module
// actively chases updates (visibility/focus/interval) and reloads exactly once when a
// new worker takes control, so a re-opened install is never stuck on stale content.

const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

// Module-scoped guard: controllerchange can in principle fire more than once (e.g. a
// second update landing right after the first reload); without this a buggy sequence
// could reload in a loop.
let hasReloadedForNewController = false;

function checkForUpdate(registration: ServiceWorkerRegistration): void {
  registration.update().catch(() => {
    // A failed update check (offline, request race during navigation, etc.) is not
    // fatal — the next trigger (focus/visibility/interval) just tries again.
  });
}

function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate(registration);
  });
  window.addEventListener('focus', () => checkForUpdate(registration));
  setInterval(() => checkForUpdate(registration), UPDATE_INTERVAL_MS);
}

export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  // Fires once a new SW has skipWaiting()'d and clientsClaim()'d itself into control.
  // Reloading is the only way an already-open page picks up the new asset graph (new
  // hashed JS/CSS filenames), so do it — but only once per page life.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hasReloadedForNewController) return;
    hasReloadedForNewController = true;
    location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => scheduleUpdateChecks(registration))
      .catch(() => {
        // Registration failures (e.g. insecure context) must not break the app.
      });
  });
}
